/**
 * اشتراك Web Push للزائر المجهول (بلا حساب).
 *
 * لماذا منفصل عن `webPush.functions.ts`؟
 *  - تلك محميّة بـ`requireSupabaseAuth` (تربط الاشتراك بـuser_id).
 *  - ٩٩٪ من زوّارنا مجهولون، وهم الشريحة التي نريد استهدافها لاحقاً،
 *    فنحفظ اشتراكهم مربوطاً بـ`visitor_id` + `session_id` بدل user_id.
 *
 * الأمان:
 *  - تحقّق zod صارم على الـendpoint والمفاتيح.
 *  - الكتابة عبر supabaseAdmin داخل الـhandler فقط (لا anon policy مفتوحة للكتابة).
 *  - upsert على endpoint → لا تكرار ولا إغراق للجدول.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const AnonSaveInput = z.object({
  endpoint: z.string().url().max(2000),
  p256dh: z.string().min(1).max(500),
  auth: z.string().min(1).max(500),
  locale: z.enum(["ar", "en"]).default("ar"),
  market: z.string().min(2).max(8).optional(),
  sessionId: z.string().min(1).max(64).optional(),
  visitorId: z.string().min(1).max(64).optional(),
  userAgent: z.string().max(500).optional(),
});

export const saveAnonWebPushSubscription = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => AnonSaveInput.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("web_push_subscriptions")
      .upsert(
        {
          endpoint: data.endpoint,
          p256dh: data.p256dh,
          auth: data.auth,
          locale: data.locale,
          market: data.market ?? null,
          session_id: data.sessionId ?? null,
          visitor_id: data.visitorId ?? null,
          user_agent: data.userAgent ?? null,
          is_active: true,
          last_seen_at: new Date().toISOString(),
        },
        { onConflict: "endpoint" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const DeleteInput = z.object({ endpoint: z.string().url().max(2000) });

export const deleteAnonWebPushSubscription = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => DeleteInput.parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // يحذف فقط الاشتراكات غير المربوطة بحساب (اشتراك المسجَّل يُحذف من دالّته المحميّة)
    const { error } = await supabaseAdmin
      .from("web_push_subscriptions")
      .delete()
      .eq("endpoint", data.endpoint)
      .is("user_id", null);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
